import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Field } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BoldTextarea } from "@/components/BoldTextarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const TermsTemplates = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/terms-templates").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    const { data } = await api.get("/settings");
    setForm({ name: "", terms: data.terms || "", warranty_terms: data.warranty_terms || "" });
  };
  const save = async () => {
    try {
      const body = { name: form.name, terms: form.terms, warranty_terms: form.warranty_terms };
      if (form.id) await api.put(`/terms-templates/${form.id}`, body); else await api.post("/terms-templates", body);
      toast.success("Template tersimpan");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}"?`)) return;
    await api.delete(`/terms-templates/${t.id}`);
    toast.success("Template dihapus");
    load();
  };

  return (
    <div className="card-premium p-6 mb-6 max-w-4xl fade-up" data-testid="terms-templates-card">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Template Syarat & Kondisi (TOP)</p>
          <p className="text-xs text-slate-500">Simpan beberapa template untuk dipilih cepat per jenis penawaran.</p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="new-terms-template-btn"><Plus className="h-4 w-4 mr-1" />Tambah Template</Button>
      </div>
      {!rows.length && <p className="text-sm text-slate-400">Belum ada template.</p>}
      <div className="divide-y divide-slate-100">
        {rows.map((t, i) => (
          <div key={t.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`terms-template-row-${i}`}>
            <div className="min-w-0">
              <p className="font-medium">{t.name}</p>
              <p className="truncate text-xs text-slate-400">{(t.terms || "").split("\n")[0]}</p>
            </div>
            <div className="flex shrink-0">
              <Button size="icon" variant="ghost" onClick={() => setForm({ ...t })} data-testid={`terms-template-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(t)} data-testid={`terms-template-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? "Edit" : "Tambah"} Template TOP</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Nama Template (contoh: Pengadaan Barang, Jasa Maintenance)"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="terms-template-name-input" /></Field>
              <Field label="Syarat & Kondisi Umum"><BoldTextarea rows={10} className="text-xs" value={form.terms} onChange={(v) => setForm({ ...form, terms: v })} testId="terms-template-terms-input" /></Field>
              <Field label="Ketentuan Umum Garansi"><BoldTextarea rows={5} className="text-xs" value={form.warranty_terms} onChange={(v) => setForm({ ...form, warranty_terms: v })} testId="terms-template-warranty-input" /></Field>
              <p className="text-[11px] text-slate-400">Format: baris diawali angka = poin bernomor, diawali "-" = sub-poin, **teks** = tebal, {"{company}"} = nama perusahaan.</p>
              <Button className="w-full" disabled={!form.name.trim()} onClick={save} data-testid="terms-template-save-btn">Simpan Template</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
